import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Upload, Database, Snowflake, ExternalLink, Lock, Receipt } from "lucide-react";

const CATEGORIES = ["POS", "CRM", "IOT", "ERP", "SOCIAL", "SUPPLY_CHAIN"];
const CONNECTORS = [
  { id: "csv", label: "CSV Upload", icon: Upload, desc: "Upload a .csv from any system. Parsed instantly." },
  { id: "snowflake", label: "Snowflake", icon: Snowflake, desc: "Account + warehouse + credentials." },
  { id: "salesforce", label: "Salesforce", icon: Database, desc: "Org URL + API token." },
  { id: "xero", label: "Xero", icon: Receipt, desc: "Accounting. Imports invoices, contacts, and chart of accounts." },
];

export default function AddSourceDialog({ open, onClose, onDone }) {
  const [connector, setConnector] = useState("csv");
  const [form, setForm] = useState({ name: "", category: "CRM", account: "", username: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [oauth, setOauth] = useState({ salesforce: false, snowflake: false, xero: false });

  useEffect(() => {
    if (!open) return;
    Promise.all([
      api.get("/connectors/oauth/salesforce/status").catch(() => ({ data: { configured: false } })),
      api.get("/connectors/oauth/snowflake/status").catch(() => ({ data: { configured: false } })),
      api.get("/connectors/xero/status").catch(() => ({ data: { configured: false } })),
    ]).then(([sf, sn, xr]) => {
      setOauth({
        salesforce: !!sf.data?.configured,
        snowflake: !!sn.data?.configured,
        xero: !!xr.data?.configured,
      });
    });
  }, [open]);

  if (!open) return null;

  const startOauth = async (provider) => {
    try {
      if (provider === "xero") {
        const { data } = await api.get("/connectors/xero/login");
        // Use window.top to break out of any embedding iframe (Emergent
        // preview pane, parent app embeds, etc.). Xero's login page sets
        // X-Frame-Options: DENY so navigating inside an iframe fails with
        // "refused to connect". Falling back to window.location keeps it
        // working when window.top is cross-origin restricted.
        const authUrl = data.authorize_url;
        try {
          if (window.top && window.top !== window.self) {
            window.top.location.href = authUrl;
            return;
          }
        } catch (_) {
          // Cross-origin frame — can't touch top, fall through.
        }
        window.location.href = authUrl;
        return;
      }
      const { data } = await api.get(`/connectors/oauth/${provider}/start`);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OAuth failed");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (connector === "xero") {
      // Xero is OAuth-only — no direct credential form.
      startOauth("xero");
      return;
    }
    setBusy(true);
    try {
      if (connector === "csv") {
        if (!file) { toast.error("Pick a CSV file"); setBusy(false); return; }
        const fd = new FormData();
        fd.append("name", form.name || file.name);
        fd.append("category", form.category);
        fd.append("file", file);
        await api.post("/connectors/csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        await api.post("/connectors", {
          origin: connector.toUpperCase(),
          name: form.name || `${connector} connection`,
          category: form.category,
          account: form.account,
          username: form.username,
          description: "",
        });
      }
      toast.success("Source connected.");
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="add-source-dialog">
      <div className="panel max-w-xl w-full">
        <header className="border-b border-zinc-900 px-5 py-3 flex items-center justify-between">
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">// ADD DATA SOURCE</span>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="close-add-source"><X className="w-4 h-4" /></button>
        </header>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {CONNECTORS.map(c => (
              <button
                key={c.id}
                type="button"
                onClick={() => setConnector(c.id)}
                data-testid={`connector-${c.id}`}
                className={`p-3 border text-left transition-colors ${connector === c.id ? "border-[#FFB000] bg-[#FFB000]/5" : "border-zinc-800 hover:border-zinc-700"}`}
              >
                <c.icon className={`w-4 h-4 mb-2 ${connector === c.id ? "text-[#FFB000]" : "text-zinc-500"}`} />
                <div className="font-mono text-[11px] text-white">{c.label}</div>
                <div className="font-mono text-[9px] text-zinc-500 mt-1 leading-snug">{c.desc}</div>
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Display name</span>
              <input data-testid="source-name" required={connector !== "xero"} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder={connector === "xero" ? "Auto-named per Xero org" : "e.g. APAC POS Feed"} disabled={connector === "xero"} className={fieldCls + (connector === "xero" ? " opacity-50 cursor-not-allowed" : "")} />
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Category</span>
              <select data-testid="source-category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} disabled={connector === "xero"} className={fieldCls + (connector === "xero" ? " opacity-50 cursor-not-allowed" : "")}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          </div>

          {connector === "csv" && (
            <div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">CSV file</span>
              <input
                data-testid="csv-file"
                type="file" accept=".csv,text/csv" required
                onChange={e => setFile(e.target.files?.[0] || null)}
                className="mt-2 w-full bg-black border border-zinc-800 px-3 py-2.5 font-mono text-xs text-zinc-400 file:bg-[#FFB000] file:text-black file:border-0 file:font-mono file:text-[10px] file:uppercase file:tracking-widest file:px-3 file:py-1.5 file:mr-3"
              />
            </div>
          )}

          {connector === "xero" && (
            <div className="space-y-3">
              {oauth.xero ? (
                <div className="border border-[#00E676]/30 bg-[#00E676]/5 p-3 flex items-center justify-between gap-3">
                  <div className="flex items-start gap-2">
                    <Lock className="w-4 h-4 text-[#00E676] mt-0.5" />
                    <div>
                      <div className="font-mono text-[11px] text-[#00E676]">XERO OAUTH READY</div>
                      <div className="font-mono text-[10px] text-zinc-500 mt-1">Sign in to Xero to import invoices, contacts, and chart of accounts.</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="border border-[#FFB000]/30 bg-[#FFB000]/5 p-3 flex items-start gap-2">
                  <Lock className="w-4 h-4 text-[#FFB000] mt-0.5" />
                  <div>
                    <div className="font-mono text-[11px] text-[#FFB000]">XERO NOT YET CONFIGURED</div>
                    <div className="font-mono text-[10px] text-zinc-400 mt-1 leading-relaxed">
                      An admin needs to register a Xero app at <span className="text-white">developer.xero.com</span> and set <code className="text-[#FFB000]">XERO_CLIENT_ID</code> + <code className="text-[#FFB000]">XERO_CLIENT_SECRET</code> in the backend environment.
                    </div>
                  </div>
                </div>
              )}
              <div className="font-mono text-[10px] text-zinc-600 bg-zinc-900/50 border border-zinc-900 p-2.5 leading-relaxed">
                ⓘ On first connect, DARA creates one data source per (Xero organisation × entity): Invoices (ERP), Contacts (CRM), and Chart of Accounts (ERP). Re-sync any time from the source detail page.
              </div>
            </div>
          )}

          {connector !== "csv" && connector !== "xero" && (
            <>
              {oauth[connector] && (
                <div className="border border-[#00E676]/30 bg-[#00E676]/5 p-3 flex items-center justify-between gap-3">
                  <div className="flex items-start gap-2">
                    <Lock className="w-4 h-4 text-[#00E676] mt-0.5" />
                    <div>
                      <div className="font-mono text-[11px] text-[#00E676]">OAUTH AVAILABLE</div>
                      <div className="font-mono text-[10px] text-zinc-500 mt-1">Sign in to {connector} for a secure, real connection.</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => startOauth(connector)}
                    data-testid={`oauth-${connector}-btn`}
                    className="bg-[#00E676] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-3 py-2 hover:bg-[#00c963] flex items-center gap-1.5 whitespace-nowrap"
                  >
                    CONNECT VIA OAUTH <ExternalLink className="w-3 h-3" />
                  </button>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Account / Org URL</span>
                  <input data-testid="source-account" required value={form.account} onChange={e => setForm({ ...form, account: e.target.value })} className={fieldCls} />
                </label>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Username / Token</span>
                  <input data-testid="source-username" required value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} type="password" className={fieldCls} />
                </label>
                <div className="col-span-2 font-mono text-[10px] text-zinc-600 bg-zinc-900/50 border border-zinc-900 p-2.5">
                  ⓘ {oauth[connector] ? "Or skip OAuth and store credentials directly below (simulated sync)." : "OAuth not configured by admin. Credentials are stored server-side; sync is simulated."}
                </div>
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-900">
            <button type="button" onClick={onClose} className="border border-zinc-700 px-4 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-zinc-900">CANCEL</button>
            <button
              type="submit"
              disabled={busy || (connector === "xero" && !oauth.xero)}
              data-testid="submit-source-btn"
              className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-2 hover:bg-[#E59E00] disabled:opacity-50"
            >{busy ? "CONNECTING…" : connector === "xero" ? "CONNECT TO XERO →" : "CONNECT SOURCE"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const fieldCls = "mt-2 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-sm text-white focus:outline-none focus:border-[#FFB000]";
